/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import type { ITemplate } from '../interfaces/templates'
import Default from './Default'
import { useCheckoutRuleOptions } from '../hooks/checkout'
import { CheckoutRules } from '../interfaces'

/**
 * Delivery-date screen, reached at whatever route COM-951 authors for it.
 *
 * Thin by design, like `PaymentMethodSelector` beside it: fetch on mount, fire
 * analytics, delegate rendering to `Default`. `DeliveryDateCalendar`, its
 * copy, its month window and its actions are authored in SCS config and
 * resolve through `importRoute`, so no layout belongs here — and the props
 * config cannot express are injected in `useCinnamonProps`.
 * `InjectedDeliveryDateProps` in `utils/deliveryDatePicker.ts` is the list,
 * checked against the library's own prop type.
 *
 * The empty and error states are authored components gated on
 * `checkout.ui.DELIVERY_DATES.{emptyHidden,errorHidden}` through a `hidden`
 * prop. This template renders no copy of its own, and its suite asserts that.
 *
 * ⚠️ **Unlike the payment picker, the calendar does not erase itself when it
 * has nothing to offer.** Given `availableDates: []` it draws a full,
 * arrow-navigable month grid with every cell `aria-disabled`, so an authored
 * empty-state message stacks on top of a live grey calendar. COM-1060 adds a
 * `hidden` prop to the calendar so config can withdraw the grid on the same
 * flag; until it ships, author the message or the grid, not both.
 *
 * ⚠️ **Expect at most one date, and plan for none.** `getCheckoutRule` passes
 * no `requestData`, so the resolver's external-API branch is unreachable and
 * every answer comes from `resolveDefaultDates` — a single computed day —
 * filtered by `validateDates` to strictly after today. A calendar is the right
 * control for what this rule *can* offer, not for what the pilot returns
 * today. Whether a one-option rule should settle itself and skip the screen is
 * COM-1048's question, not this file's.
 *
 * ⚠️ **R-8: the day token goes out exactly as it came in.** The storefront
 * matches by strict string equality, so a token parsed into a `Date` and
 * re-serialised is rejected for the same day the shopper tapped. The calendar
 * echoes the caller's string byte for byte and the adapter normalises nothing.
 *
 * ⚠️ **The tenant's timezone anchors the month, never the dates.** It reaches
 * the calendar as `referenceDate`, which is the one value here that reads a
 * clock. The calendar has no `timeZone` prop by design: a delivery date names
 * a calendar day and renders as stored, so applying a zone west of UTC moves
 * it to the previous day — COM-816 rather than its fix.
 *
 * **No route exists in code.** Cinnamon builds its routes from the SCS
 * config's own `routes` array, and `defaultConfig` ships none for this. The
 * screen is unreachable until a storefront authors the route, the container
 * and a `pushView` onto it — COM-951 does that for the pilot.
 */
const DeliveryDatePicker: FC<ITemplate> = ({ ...props }) => {
  useCheckoutRuleOptions(CheckoutRules.DELIVERY_DATES)

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: `/${props.container.uid}` })
    window.fbq('track', 'PageView', { page: `/${props.container.uid}` })
  }, [])

  return <Default {...props} />
}

export default DeliveryDatePicker
